import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Link,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BugReportIcon from "@mui/icons-material/BugReport";
import CodeIcon from "@mui/icons-material/Code";
import SettingsIcon from "@mui/icons-material/Settings";
import { useHelpQuery } from "../useQuery";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink } from "react-router-dom";
import MenuBookIcon from "@mui/icons-material/MenuBook";

const MessageBubble = ({ message, isUser, isLoading }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      mb: 2,
    }}
  >
    <Paper
      elevation={1}
      sx={{
        p: 2,
        maxWidth: "70%",
        backgroundColor: isUser ? "#FF5336" : "#f5f7f9",
        color: isUser ? "#ffffff" : "#000000",
        borderRadius: 2,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        "& .markdown-content": {
          "& p": {
            margin: 0,
            marginBottom: "0.5em",
            "&:last-child": {
              marginBottom: 0,
            },
          },
          "& ul, & ol": {
            margin: "0.5em 0",
            paddingLeft: "1.5em",
          },
          "& li": {
            margin: "0.25em 0",
          },
          "& code": {
            backgroundColor: isUser ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
            padding: "0.2em 0.4em",
            borderRadius: "3px",
            fontSize: "0.9em",
          },
          "& pre": {
            backgroundColor: isUser ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
            padding: "1em",
            borderRadius: "4px",
            overflowX: "auto",
            margin: "0.5em 0",
            "& code": {
              backgroundColor: "transparent",
              padding: 0,
            },
          },
        },
      }}
    >
      {isLoading && !isUser && (
        <CircularProgress size={16} sx={{ color: "#8089A8", mt: 0.5 }} />
      )}
      {isUser ? (
        <Typography variant="body1">{message}</Typography>
      ) : (
        <Box className="markdown-content">
          <ReactMarkdown>{message}</ReactMarkdown>
        </Box>
      )}
    </Paper>
  </Box>
);

const PromptCard = ({ title, description, icon: Icon, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: "100%",
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      border: "1px solid #e2e6f3",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        borderColor: "#FF5336",
      },
    }}
  >
    <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: "#f5f7f9",
            borderRadius: "12px",
            p: 1,
            mr: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ color: "#FF5336", fontSize: 24 }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#242f3f" }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "#666", flex: 1 }}>
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const HelpCenter = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef(null);

  const predefinedPrompts = [
    {
      title: "Getting Started",
      description: "Learn how to create and run your first test case",
      icon: HelpOutlineIcon,
      prompt: "How do I create and run my first test case?",
    },
    {
      title: "Debugging Tests",
      description: "Troubleshoot and fix common test failures",
      icon: BugReportIcon,
      prompt: "How do I debug a failed test case?",
    },
    {
      title: "API Testing",
      description: "Learn about API testing features and best practices",
      icon: CodeIcon,
      prompt: "How do I test API endpoints?",
    },
    {
      title: "Configuration",
      description: "Set up and customize your testing environment",
      icon: SettingsIcon,
      prompt: "How do I configure my test environment?",
    },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing]);

  const helpMutation = useHelpQuery({
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        { 
          text: data.response || "I'm sorry, I couldn't find an answer to your question.", 
          isUser: false 
        },
      ]);
      setIsProcessing(false);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        { 
          text: "Sorry, there was an error processing your request. Please try again.", 
          isUser: false 
        },
      ]);
      setIsProcessing(false);
    },
  });

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages((prev) => [...prev, { text: userMessage, isUser: true }]);
    setInput("");
    setIsProcessing(true);

    helpMutation.mutate({ userQuery: userMessage });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePromptClick = (prompt) => {
    setInput(prompt);
    setTimeout(() => handleSend(), 100);
  };

  return (
    <Box sx={{ p: 3, height: "calc(100vh - 64px)", display: "flex", flexDirection: "column" }}>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          mb: 2,
          p: 2,
          backgroundColor: "#ffffff",
          borderRadius: 2,
          border: "1px solid #e2e6f3",
          overflow: "auto",
        }}
      >
        {messages.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                color: "#242f3f",
                textAlign: "center",
                fontWeight: 500
              }}
            >
              How can I help you today?
            </Typography>
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Link
                component={RouterLink}
                to="/new/documentation"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#FF5336",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                <MenuBookIcon />
                <Typography variant="body1">
                  View Full Documentation
                </Typography>
              </Link>
            </Box>
            <Grid container spacing={2}>
              {predefinedPrompts.map((prompt, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <PromptCard
                    title={prompt.title}
                    description={prompt.description}
                    icon={prompt.icon}
                    onClick={() => handlePromptClick(prompt.prompt)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble 
                key={index} 
                message={message.text} 
                isUser={message.isUser} 
              />
            ))}
            {isProcessing && (
              <MessageBubble 
                message="Thinking..." 
                isUser={false} 
                isLoading={true} 
              />
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Paper>

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your question here..."
          variant="outlined"
          disabled={isProcessing}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#ffffff",
              borderRadius: 2,
              "& fieldset": {
                borderColor: "#e2e6f3",
              },
              "&:hover fieldset": {
                borderColor: "#FF5336",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FF5336",
              },
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || isProcessing}
          sx={{
            width: 48,
            height: 48,
            minWidth: 48,
            mt: 1,
            backgroundColor: "#FF5336",
            color: "#ffffff",
            borderRadius: "50%",
            "&:hover": {
              backgroundColor: "#e64a2e",
            },
            "&.Mui-disabled": {
              backgroundColor: "#ffcdd2",
              color: "#ffffff",
            },
            "& .MuiSvgIcon-root": {
              fontSize: 24,
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#e64a2e",
              transform: "scale(1.05)",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          {isProcessing ? (
            <CircularProgress 
              size={24} 
              sx={{ 
                color: "#ffffff",
                "& .MuiCircularProgress-circle": {
                  strokeWidth: 2.5
                }
              }} 
            />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default HelpCenter; 